import React, { useState, useEffect, useMemo } from 'react';
import { Video, User, X, Clock, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import VideoPlayer from './VideoPlayer';
import { useAllUsers } from '../hooks/queries';
import { userKeys } from '../hooks/queries/useUsers';
import { videoService } from '../services/videoService';

export default function VideoLibrary() {
  const queryClient = useQueryClient();
  const [uploadedVideos, setUploadedVideos] = useState([]);
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [selectedClients, setSelectedClients] = useState({}); // Track selected client for each video
  const [searchTerm, setSearchTerm] = useState(''); // Search term for filtering clients
  const [assigningVideos, setAssigningVideos] = useState({}); // Track which videos are being assigned
  const [checkingStatus, setCheckingStatus] = useState({}); // Track which videos are being status checked
  const [videoStatuses, setVideoStatuses] = useState({}); // Store video processing statuses
  const [checkingAllStatuses, setCheckingAllStatuses] = useState(false); // Track when checking all statuses

  // Fetch all users for client assignment dropdown
  const { data: allUsersData, isLoading: usersLoading, error: usersError } = useAllUsers();
  
  // Extract users from API response and format for dropdown
  const assignedClients = useMemo(() => {
    if (!allUsersData || !Array.isArray(allUsersData)) return [];
    
    return allUsersData.map(user => ({
      id: user.id,
      name: user.name || `User #${user.id}`,
      email: user.email || '',
    })).sort((a, b) => a.name.localeCompare(b.name));
  }, [allUsersData]);

  // Filter clients based on search term
  const filteredClients = useMemo(() => {
    if (!searchTerm.trim()) return assignedClients;
    
    const term = searchTerm.toLowerCase();
    return assignedClients.filter(client => 
      client.name.toLowerCase().includes(term) || 
      client.email.toLowerCase().includes(term)
    );
  }, [assignedClients, searchTerm]);

  // Load uploaded videos from localStorage on component mount
  useEffect(() => {
    const savedUploadedVideos = localStorage.getItem('uploadedVideos');
    if (savedUploadedVideos) {
      setUploadedVideos(JSON.parse(savedUploadedVideos));
    }
  }, []);

  // Check video processing status for all videos when component mounts or videos change
  // Note: Only checks videos that are still in "processing" state to optimize API calls
  useEffect(() => {
    if (uploadedVideos.length > 0) {
      checkAllVideoStatuses();
    }
  }, [uploadedVideos.length]); // Only run when the number of videos changes

  const saveUploadedVideos = (videos) => {
    localStorage.setItem('uploadedVideos', JSON.stringify(videos));
  };

  const clearUploadedVideos = () => {
    localStorage.removeItem('uploadedVideos');
    setUploadedVideos([]);
    setVideoStatuses({});
  };

  // Check processing status for a single video
  const checkVideoStatus = async (video, videoIndex) => {
    const status = getCurrentVideoStatus(video, videoIndex);
    
    // Skip checking if both statuses are already completed or failed (not processing)
    if (status.transcription_status !== 'processing' && status.splitting_status !== 'processing') {
      return;
    }

    setCheckingStatus(prev => ({ ...prev, [videoIndex]: true }));
    
    try {
      const response = await videoService.checkVideoStatus(video.filename);
      
      if (response && response.data) {
        const statusData = response.data;
        
        // Update video statuses state
        setVideoStatuses(prev => ({
          ...prev,
          [video.filename]: {
            transcription_status: statusData.transcription_status,
            splitting_status: statusData.splitting_status
          }
        }));

        // Update localStorage with new status
        const updatedVideos = uploadedVideos.map((v, index) => 
          index === videoIndex 
            ? { 
                ...v, 
                transcription_status: statusData.transcription_status,
                splitting_status: statusData.splitting_status
              }
            : v
        );
        setUploadedVideos(updatedVideos);
        saveUploadedVideos(updatedVideos);

        // Show error toasts for failed statuses
        if (statusData.transcription_status === 'failed' || statusData.splitting_status === 'failed') {
          const failedProcesses = [];
          if (statusData.transcription_status === 'failed') failedProcesses.push('transcription');
          if (statusData.splitting_status === 'failed') failedProcesses.push('splitting');
          
          toast.error(`Video processing failed: ${failedProcesses.join(' and ')} failed for "${video.name}"`);
        }
      }
    } catch (error) {
      console.error('Error checking video status:', error);
      toast.error(`Failed to check status for "${video.name}"`);
    } finally {
      setCheckingStatus(prev => {
        const updated = { ...prev };
        delete updated[videoIndex];
        return updated;
      });
    }
  };

  // Check status for all videos (only processing ones)
  const checkAllVideoStatuses = async () => {
    setCheckingAllStatuses(true);
    
    try {
      const processingVideos = uploadedVideos.filter((video, index) => {
        const status = getCurrentVideoStatus(video, index);
        return status.transcription_status === 'processing' || status.splitting_status === 'processing';
      });

      if (processingVideos.length === 0) {
        // toast.info('No videos currently processing');
        return;
      }

      const promises = processingVideos.map((video) => {
        const videoIndex = uploadedVideos.findIndex(v => v.filename === video.filename);
        return checkVideoStatus(video, videoIndex);
      });
      
      await Promise.all(promises);
      // toast.success(`Checked status for ${processingVideos.length} video(s)`);
    } catch (error) {
      toast.error('Failed to check video statuses');
    } finally {
      setCheckingAllStatuses(false);
    }
  };

  // Get current status for a video (from state or localStorage)
  const getCurrentVideoStatus = (video, videoIndex) => {
    const stateStatus = videoStatuses[video.filename];
    const localStatus = {
      transcription_status: video.transcription_status,
      splitting_status: video.splitting_status
    };
    
    return stateStatus || localStatus;
  };

  // Check if video processing is complete
  const isVideoProcessingComplete = (video, videoIndex) => {
    const status = getCurrentVideoStatus(video, videoIndex);
    return status.transcription_status === 'completed' && status.splitting_status === 'completed';
  };

  // Check if video processing has failed
  const hasVideoProcessingFailed = (video, videoIndex) => {
    const status = getCurrentVideoStatus(video, videoIndex);
    return status.transcription_status === 'failed' || status.splitting_status === 'failed';
  };

  // Get processing status message
  const getProcessingStatusMessage = (video, videoIndex) => {
    const status = getCurrentVideoStatus(video, videoIndex);
    
    if (status.transcription_status === 'failed' && status.splitting_status === 'failed') {
      return 'Both transcription and splitting failed';
    } else if (status.transcription_status === 'failed') {
      return 'Transcription failed';
    } else if (status.splitting_status === 'failed') {
      return 'Splitting failed';
    } else if (status.transcription_status === 'processing' || status.splitting_status === 'processing') {
      const processing = [];
      if (status.transcription_status === 'processing') processing.push('transcription');
      if (status.splitting_status === 'processing') processing.push('splitting');
      return `Video is processing... (${processing.join(' and ')})`;
    }
    
    return 'Ready for assignment';
  };

  // Count videos that are still processing
  const getProcessingVideosCount = () => {
    return uploadedVideos.filter((video, index) => {
      const status = getCurrentVideoStatus(video, index);
      return status.transcription_status === 'processing' || status.splitting_status === 'processing';
    }).length;
  };

  // Handle client selection (doesn't call API yet)
  const selectClient = (videoIndex, user) => {
    setSelectedClients(prev => ({
      ...prev,
      [videoIndex]: user
    }));
    setEditingAssignment(null);
  };

  // Actually assign the video to the selected client
  const assignVideoToClient = async (videoIndex) => {
    const selectedClient = selectedClients[videoIndex];
    if (!selectedClient) return;

    const video = uploadedVideos[videoIndex];
    
    // Set loading state for this specific video
    setAssigningVideos(prev => ({ ...prev, [videoIndex]: true }));
    
    try {
      const response = await videoService.assignUserToVideo(video.filename, selectedClient.id);
      
      if (response) {
        // Assignment successful, remove video from localStorage
        const updatedVideos = uploadedVideos.filter((_, index) => index !== videoIndex);
        setUploadedVideos(updatedVideos);
        saveUploadedVideos(updatedVideos);
        
        // Clear the selected client for this video
        setSelectedClients(prev => {
          const updated = { ...prev };
          delete updated[videoIndex];
          return updated;
        });
        
        toast.success('Assignment Successful!');
        
        // Invalidate users cache to refresh dropdown data
        queryClient.invalidateQueries({ queryKey: userKeys.allUsers() });
      }
    } catch (error) {
      console.error('Error assigning video to user:', error);
      toast.error('Assignment Failed');
    } finally {
      // Clear loading state for this video
      setAssigningVideos(prev => {
        const updated = { ...prev };
        delete updated[videoIndex];
        return updated;
      });
    }
  };

  // Clear selected client for a video
  const clearSelectedClient = (videoIndex) => {
    setSelectedClients(prev => {
      const updated = { ...prev };
      delete updated[videoIndex];
      return updated;
    });
  };

  const removeVideo = (videoIndex) => {
    if (confirm('Are you sure you want to remove this video from the list?')) {
      const updatedVideos = uploadedVideos.filter((_, index) => index !== videoIndex);
      setUploadedVideos(updatedVideos);
      saveUploadedVideos(updatedVideos);
    }
  };

  // Close dropdown and clear search
  const closeDropdown = () => {
    setEditingAssignment(null);
    setSearchTerm('');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-foreground mb-2">Video Library</h2>
          <p className="text-muted-foreground">Manage your uploaded videos and assign them to clients.</p>
        </div>
        {uploadedVideos.length > 0 && (
          <div className="flex space-x-2">
            <button
              onClick={checkAllVideoStatuses}
              disabled={checkingAllStatuses}
              className="px-4 py-2 text-sm bg-primary hover:bg-primary/90 disabled:bg-primary/50 disabled:cursor-not-allowed text-white rounded-lg transition-colors duration-200 flex items-center space-x-2"
            >
              {checkingAllStatuses && (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              )}
              <span>
                {checkingAllStatuses 
                  ? 'Checking...' 
                  : `Refresh Status${getProcessingVideosCount() > 0 ? ` (${getProcessingVideosCount()} processing)` : ''}`
                }
              </span>
            </button>
                      <button
              onClick={clearUploadedVideos}
              className="px-4 py-2 text-sm bg-primary hover:bg-primary/90 text-white rounded-lg transition-colors duration-200"
            >
            Clear All Videos
          </button>
          </div>
        )}
      </div>

      {/* Video Library */}
      {uploadedVideos.length === 0 ? (
        <div className="text-center py-12 bg-background border border-secondary-border rounded-xl">
          <Video className="mx-auto h-16 w-16 text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">No Videos Yet</h3>
          <p className="text-muted-foreground mb-4">Upload some videos to get started with your library.</p>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {uploadedVideos.map((video, index) => (
            <div key={index} className="bg-background border border-secondary-border rounded-lg overflow-hidden hover:shadow-md transition-all duration-200">
              <div className="aspect-video bg-gray-900 overflow-hidden">
                <VideoPlayer filename={video.filename} className="w-full h-full" />
              </div>
              <div className="p-4">
                <div className="mb-3">
                  <h4 className="font-medium text-foreground text-base mb-1 truncate" title={video.name}>
                    {video.name}
                  </h4>
                  <div className="flex items-center text-xs text-muted-foreground space-x-2">
                    <span>{(video.size / (1024 * 1024)).toFixed(2)} MB</span>
                    <span>•</span>
                    <span>{new Date(video.uploadedAt).toLocaleDateString()}</span>
                  </div>
                </div>
                
                {/* Assignment Section */}
                <div className="mb-3 p-3 bg-background border border-secondary-border rounded-md">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-foreground text-xs">Assign to Client</span>
                    {checkingStatus[index] && (
                      <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                    )}
                  </div>
                  
                  {/* Processing Status Display */}
                  {!isVideoProcessingComplete(video, index) && (
                    <div className={`p-2 rounded-md mb-3 ${
                      hasVideoProcessingFailed(video, index) 
                        ? 'bg-secondary border border-primary/30' 
                        : 'bg-secondary border border-secondary-border'
                    }`}>
                      <div className="flex items-center space-x-2">
                        {hasVideoProcessingFailed(video, index) ? (
                          <AlertTriangle className="h-4 w-4 text-primary" />
                        ) : (
                          <Clock className="h-4 w-4 text-primary/70" />
                        )}
                        <span className={`text-xs font-medium ${
                          hasVideoProcessingFailed(video, index) ? 'text-primary' : 'text-muted-foreground'
                        }`}>
                          {getProcessingStatusMessage(video, index)}
                        </span>
                      </div>
                      {hasVideoProcessingFailed(video, index) ? (
                        <button
                          onClick={() => removeVideo(index)}
                          className="mt-2 text-xs text-primary hover:text-primary/80 underline"
                        >
                          Remove failed video
                        </button>
                      ) : !isVideoProcessingComplete(video, index) && (
                        <button
                          onClick={() => checkVideoStatus(video, index)}
                          disabled={checkingStatus[index]}
                          className="mt-2 text-xs text-primary hover:text-primary/80 underline disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {checkingStatus[index] ? 'Checking...' : 'Refresh Status'}
                        </button>
                      )}
                    </div>
                  )}
                  
                  {/* Only show assignment UI if processing is complete */}
                  {isVideoProcessingComplete(video, index) && selectedClients[index] ? (
                    <div className="space-y-3">
                      {/* Selected Client Display */}
                      <div className="flex items-center space-x-2 p-2 bg-secondary border border-primary/30 rounded">
                        <div className="w-7 h-7 bg-primary rounded-full flex items-center justify-center">
                          <User className="h-3 w-3 text-white" />
                        </div>
                        <div className="flex-1">
                          <div className="font-medium text-foreground text-sm">{selectedClients[index].name}</div>
                          <div className="text-xs text-muted-foreground truncate max-w-[120px]">{selectedClients[index].email}</div>
                        </div>
                      </div>
                      
                      {/* Action Buttons */}
                      <div className="flex space-x-2">
                        <button
                          onClick={() => assignVideoToClient(index)}
                          disabled={assigningVideos[index]}
                          className="flex-1 py-2 px-3 bg-primary hover:bg-primary/80 disabled:bg-primary/50 disabled:cursor-not-allowed text-white rounded font-medium transition-colors text-sm flex items-center justify-center min-h-[36px]"
                        >
                          {assigningVideos[index] ? (
                            <div className="flex items-center space-x-2">
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                              <span>Assigning...</span>
                            </div>
                          ) : (
                            'Assign Video'
                          )}
                        </button>
                        <button
                          onClick={() => {
                            setEditingAssignment(index);
                            setSearchTerm('');
                          }}
                          className="px-3 py-2 border border-secondary-border rounded text-muted-foreground hover:bg-secondary transition-colors text-sm"
                        >
                          Change
                        </button>
                        <button
                          onClick={() => clearSelectedClient(index)}
                          className="px-3 py-2 border border-primary/30 text-primary hover:bg-secondary rounded transition-colors text-sm"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : isVideoProcessingComplete(video, index) ? (
                    <button
                      onClick={() => {
                        setEditingAssignment(index);
                        setSearchTerm('');
                      }}
                      className="w-full py-2 px-3 border-2 border-dashed border-secondary-border rounded text-muted-foreground hover:bg-primary hover:text-white hover:border-primary/50 transition-all text-sm"
                    >
                      + Select Client
                    </button>
                  ) : null}
                  
                  {/* Assignment Dropdown - only show if processing complete */}
                  {isVideoProcessingComplete(video, index) && editingAssignment === index && (
                    <div className="mt-2 border border-secondary-border rounded bg-secondary shadow-lg z-10">
                      {/* Search Input */}
                      <div className="p-3 border-b border-secondary-border">
                        <input
                          type="text"
                          placeholder="Search clients..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-secondary-border rounded focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary bg-background"
                          autoFocus
                        />
                      </div>
                      
                      <div className="max-h-40 overflow-y-auto">
                        <button
                          onClick={() => {
                            clearSelectedClient(index);
                            closeDropdown();
                          }}
                          className="w-full px-3 py-2 text-left hover:bg-primary hover:text-white text-muted-foreground border-b border-secondary-border text-sm"
                        >
                          No selection
                        </button>
                        {usersLoading ? (
                          <div className="px-3 py-2 text-center text-sm text-muted-foreground">
                            Loading users...
                          </div>
                        ) : usersError ? (
                          <div className="px-3 py-2 text-center text-sm text-red-500">
                            Error loading users
                          </div>
                        ) : filteredClients.length === 0 && assignedClients.length > 0 ? (
                          <div className="px-3 py-2 text-center text-sm text-muted-foreground">
                            No clients found matching &quot;{searchTerm}&quot;
                          </div>
                        ) : assignedClients.length === 0 ? (
                          <div className="px-3 py-2 text-center text-sm text-muted-foreground">
                            No users available
                          </div>
                        ) : (
                          filteredClients.map((client) => (
                            <button
                              key={client.id}
                              onClick={() => {
                                selectClient(index, client);
                              }}
                              className="w-full px-3 py-2 text-left hover:bg-primary hover:text-white flex items-center space-x-2 border-b border-secondary-border last:border-b-0"
                            >
                              <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                                <User className="h-3 w-3 text-white" />
                              </div>
                              <div className="hover:text-white">
                                <div className="font-medium text-foreground text-sm">{client.name}</div>
                                <div className="text-xs text-muted-foreground">{client.email}</div>
                              </div>
                            </button>
                          ))
                        )}
                      </div>
                      <div className="p-2 border-t border-secondary-border bg-secondary hover:bg-primary">
                        <button
                          onClick={closeDropdown}
                          className="w-full py-1 text-xs text-muted-foreground hover:text-white"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Action Buttons */}
                <div className="flex justify-end pt-2">
                  <button
                    onClick={() => removeVideo(index)}
                    className="bg-primary hover:bg-primary/90 text-white py-1 px-3 rounded font-medium transition-colors text-xs"
                  >
                    Remove
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
} 